import moment from 'moment'
import { counterService } from '../../../services/counterService'
import {
  COMPUTED_OPERATION_TYPES,
  COMPUTED_RESERVED_KEYS,
  FIELD_TYPES,
  FORMATS,
} from '../../../utility/constants'
import { getCurrentFinancialYear } from '../../../utility/utilityFunctions'
import { getDefaultValueForField } from './fieldUtil'
import { getFieldId, getIdFromFieldId, getSelectableTableId, getTableFieldId } from './utils'
import { processDataService } from '../../../services/processDataService'
import { indexedDbStorage } from '../../../utility/indexedDbStorage'

const DATE_COMPUTATION_FIELDS = {
  FIELD: 'field',
  ACTIVITY: 'activity',
  LIST: 'list',
}

const DATE_COMPUTATION_DAYS_CATEGORY = {
  CALENDER_DAYS: 'calender-days',
  WORKING_DAYS: 'working-days',
}

const ACTIVITY_VERSION = {
  INITIAL_VERSION: 'initial-version',
  LATEST_VERSION: 'latest-version',
}

export const DROPDOWN_DEFAULT_VALUE = -1

const getFieldValueSync = ({ field, data }) => {
  const fieldKey = getFieldId(field?.id)

  let returnValue =
    data?.rowValues && fieldKey in data.rowValues
      ? data.rowValues[fieldKey]
      : data?.allValues?.[fieldKey]

  if (
    (returnValue === undefined || returnValue === null) &&
    [FIELD_TYPES.CURRENCY, FIELD_TYPES.NUMBER].includes(field?.data_structure)
  ) {
    returnValue = 0
  }

  return returnValue
}

export const autoComputeFieldValueOptimized = async ({
  idFieldMap = {},
  data = { globalValues: {}, rowValues: {} },
  listDataIdMetadataMap,
  computeFields = [],
  tableSubfieldsMap,
  selectedTableData,
  dynamicListData,
  context,
  user,
  tableRowIndex,
  allActivitiesCreatedAtWithVersion,
  holidayList,
  allProcessData,
}) => {
  // declare and initialize empty object of computed fields allComputedFieldValues
  let allComputedFieldValues = {}

  // find all computed field
  let allComputedFields = computeFields

  // loop through each computed fields
  for (const field of allComputedFields.filter(
    (it) => it.metadata.operation !== COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS,
  )) {
    // get metadata
    const fieldMetadata = field.metadata

    const altComputationFieldTemplateId = fieldMetadata?.alt_field_template_id

    const altComputationListDataIds = fieldMetadata?.alt?.reduce((acc, curr) => {
      return acc.concat(curr.list_data_ids)
    }, [])

    let fieldMetaDataParams = fieldMetadata?.params
    let fieldMetaDataOperation = fieldMetadata?.operation

    if (altComputationFieldTemplateId != null && altComputationListDataIds != null) {
      const altComputationFieldValue = data?.allValues[getFieldId(altComputationFieldTemplateId)]
      if (
        altComputationFieldValue &&
        altComputationListDataIds.includes(altComputationFieldValue)
      ) {
        fieldMetadata.alt.forEach((altObj) => {
          if (altObj.list_data_ids.includes(altComputationFieldValue)) {
            fieldMetaDataParams = altObj?.params || []
            fieldMetaDataOperation = altObj?.operation_alt
          }
        })
      }
    }

    let computedValue = getDefaultComputeFieldValue(fieldMetaDataOperation)

    if (
      fieldMetaDataOperation !== COMPUTED_OPERATION_TYPES.DATE_COMPUTATION &&
      fieldMetaDataOperation !== COMPUTED_OPERATION_TYPES.DATE_DIFF
    ) {
      for (const param of fieldMetaDataParams) {
        let computeValue = getDefaultValueForField(field)

        if (
          fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.SUM_TABLE_COLUMN ||
          fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN ||
          fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.CONCAT_TABLE_COLUMN
        ) {
          computeValue = await getParamComputeValueTableColumn({
            param,
            operation: fieldMetaDataOperation,
            fieldMetadata,
            idFieldMap,
            data,
            listDataIdMetadataMap,
            tableSubfieldsMap,
            selectedTableData,
            dynamicListData,
          })
        } else if (
          fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.SUM ||
          fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.MULTIPLY ||
          fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.CONCAT
        ) {
          computeValue = await getParamComputeValue({
            param,
            operation: fieldMetaDataOperation,
            field,
            idFieldMap,
            data,
            listDataIdMetadataMap,
            dynamicListData,
            context,
            user,
            tableRowIndex,
          })
        }

        if (fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS) {
          computedValue = computeValue
        } else {
          if (
            fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.MULTIPLY ||
            fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN
          ) {
            computedValue = computedValue * computeValue
          } else {
            if (
              (fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.CONCAT ||
                fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.CONCAT_TABLE_COLUMN) &&
              (computeValue == undefined || computeValue == 'undefined')
            ) {
              computedValue = computedValue + ''
            } else {
              computedValue = computedValue + computeValue
            }
          }
        }
      }
    }

    // DATE_COMPUTATION / DATE_DIFF handled separately — no params needed
    if (fieldMetaDataOperation === COMPUTED_OPERATION_TYPES.DATE_COMPUTATION) {
      computedValue = await computeDateField({
        fieldMetadata,
        allValues: data?.allValues,
        listDataIdMetadataMap,
        allActivitiesCreatedAtWithVersion,
        user,
        context,
        holidayList,
      })
    } else if (fieldMetaDataOperation === COMPUTED_OPERATION_TYPES.DATE_DIFF) {
      computedValue = await computeDateDiffField({
        fieldMetadata,
        allValues: data?.allValues,
        allActivitiesCreatedAtWithVersion,
        user,
        context,
        holidayList,
      })
    }

    if (fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS) {
      if (allProcessData) {
        allComputedFieldValues[getFieldId(field.id)] = computedValue
      }
    } else {
      allComputedFieldValues[getFieldId(field.id)] = computedValue
    }
  }

  return allComputedFieldValues
}

// Callers can check if a changed field affects any DATE_COMPUTATION or DATE_DIFF field
export const getDateComputationDependencyFieldIds = (allFields = []) => {
  const deps = new Set()
  allFields
    .filter(
      (f) => f.is_computed && f.metadata?.operation === COMPUTED_OPERATION_TYPES.DATE_COMPUTATION,
    )
    .forEach((f) => {
      const dc = f.metadata?.date_config
      if (dc?.source_date?.field_template_id) {
        deps.add(String(dc.source_date.field_template_id))
      }
      if (dc?.duration?.list_field_template_id) {
        deps.add(String(dc.duration.list_field_template_id))
      }
      if (dc?.duration?.numeric_field_template_id) {
        deps.add(String(dc.duration.numeric_field_template_id))
      }
    })

  allFields
    .filter((f) => f.is_computed && f.metadata?.operation === COMPUTED_OPERATION_TYPES.DATE_DIFF)
    .forEach((f) => {
      const ddc = f.metadata?.date_diff_config
      if (ddc?.date_a?.field_template_id) {
        deps.add(String(ddc.date_a.field_template_id))
      }
      if (ddc?.date_b?.field_template_id) {
        deps.add(String(ddc.date_b.field_template_id))
      }
    })

  return deps
}
const getParamComputeValueTableColumn = async ({
  param,
  operation,
  idFieldMap,
  data,
  listDataIdMetadataMap,
  tableSubfieldsMap,
  selectedTableData,
  dynamicListData,
}) => {
  let computeValue = [COMPUTED_OPERATION_TYPES.CONCAT_TABLE_COLUMN].includes(operation) ? '' : 0
  if (param?.startsWith('^^')) {
    let fieldId
    let plusOrMinusOne = 1
    const keyProp = param.substring(2).split('.')[1]

    if (
      operation === COMPUTED_OPERATION_TYPES.MULTIPLY ||
      operation === COMPUTED_OPERATION_TYPES.SUM
    ) {
      fieldId = param.substring(2).split('.')[0]
      plusOrMinusOne = param.substring(2).split('.')[0].charAt(0) === '-' ? -1 : 1
    } else {
      fieldId = param.substring(2).split('.')[0]
    }

    const tableFieldTemplateId = tableSubfieldsMap?.find(
      (it) => it.subfield_template_id == fieldId,
    )?.table_field_template_id

    // const tableField = allFields.find((it) => it.id == tableFieldTemplateId)
    // const field = allFields.find((it) => it.id == fieldId)
    const tableField = idFieldMap[tableFieldTemplateId]
    const field = idFieldMap[fieldId]

    if (tableField?.is_selectable_table) {
      for (const [key, value] of Object.entries(data?.allValues)) {
        if (
          key.includes(`f_${fieldId}__`) &&
          selectedTableData[getSelectableTableId(tableFieldTemplateId)]?.includes(
            key.split('__')[1],
          )
        ) {
          let rowValue = value
          if (
            field?.data_structure === FIELD_TYPES.LIST ||
            field?.data_structure === FIELD_TYPES.LIST_MULTI
          ) {
            const fakeAllValue = { [`f_${fieldId}`]: value }

            if (
              field?.data_structure !== FIELD_TYPES.LIST &&
              field?.data_structure !== FIELD_TYPES.LIST_MULTI
            ) {
              rowValue = getFieldValueSync({
                key,
                field,
                data: { allValues: fakeAllValue },
                listDataIdMetadataMap,
                dynamicListData,
              })
            }

            rowValue = await getFieldValue({
              key: keyProp,
              field,
              data: { allValues: fakeAllValue, rowValues: data?.rowValues },
              listDataIdMetadataMap,
              dynamicListData,
            })
          }

          // incorporate mathematical operator as per config and return
          if (operation === COMPUTED_OPERATION_TYPES.SUM_TABLE_COLUMN) {
            computeValue += plusOrMinusOne * (Number(rowValue) || 0)
          } else {
            computeValue += rowValue !== undefined && rowValue !== null ? rowValue : ''
          }
        }
      }
    } else {
      for (const [key, value] of Object.entries(data?.allValues)) {
        if (key.includes(`f_${fieldId}__`)) {
          let rowValue = value
          if (
            field?.data_structure === FIELD_TYPES.LIST ||
            field?.data_structure === FIELD_TYPES.LIST_MULTI
          ) {
            const fakeAllValue = { [`f_${fieldId}`]: value }
            if (
              field?.data_structure !== FIELD_TYPES.LIST &&
              field?.data_structure !== FIELD_TYPES.LIST_MULTI
            ) {
              rowValue = getFieldValueSync({
                key,
                field,
                data: { ...data, allValues: fakeAllValue },
                listDataIdMetadataMap,
                dynamicListData,
              })
            }

            rowValue = await getFieldValue({
              key: keyProp,
              field,
              data: { ...data, allValues: fakeAllValue },
              listDataIdMetadataMap,
              dynamicListData,
            })
          }

          // incorporate mathematical operator as per config and return
          if (operation === COMPUTED_OPERATION_TYPES.SUM_TABLE_COLUMN) {
            computeValue += plusOrMinusOne * (Number(rowValue) || 0)
          } else {
            computeValue += rowValue !== undefined && rowValue !== null ? rowValue : ''
          }
        }
      }
    }
  } else {
    console.log('Error: missing implementation of param type: ' + param)
  }
  return computeValue
}

const getParamComputeValue = async ({
  param,
  operation,
  field,
  idFieldMap,
  data,
  listDataIdMetadataMap,
  dynamicListData,
  context,
  user,
  tableRowIndex,
}) => {
  try {
    if (param?.startsWith('^^')) {
      /* this section reference values from other fields */
      let fieldId
      let plusOrMinusOne = 1
      const key = param.substring(2).split('.')[1]

      if (
        operation === COMPUTED_OPERATION_TYPES.MULTIPLY ||
        operation === COMPUTED_OPERATION_TYPES.SUM
      ) {
        fieldId = param.substring(3).split('.')[0]
        plusOrMinusOne = param.substring(2).split('.')[0].charAt(0) === '-' ? -1 : 1
      } else {
        fieldId = param.substring(2).split('.')[0]
      }

      const field = idFieldMap[fieldId]

      let fieldValue
      if (
        field?.data_structure !== FIELD_TYPES.LIST &&
        field?.data_structure !== FIELD_TYPES.LIST_MULTI
      ) {
        fieldValue = getFieldValueSync({
          key,
          field,
          data,
          listDataIdMetadataMap,
          dynamicListData,
        })
      } else {
        fieldValue = await getFieldValue({
          key: key,
          field,
          data,
          listDataIdMetadataMap,
          dynamicListData,
        })
      }

      // incorporate mathematical operator as per config and return
      if (
        operation === COMPUTED_OPERATION_TYPES.MULTIPLY ||
        operation === COMPUTED_OPERATION_TYPES.SUM ||
        operation === COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN ||
        operation === COMPUTED_OPERATION_TYPES.SUM_TABLE_COLUMN
      ) {
        return plusOrMinusOne * fieldValue
      } else {
        return fieldValue // simply return for concat
      }
    } else if (param?.startsWith('**')) {
      /* this section have some fixed value (processReferenceNum, currentFinancialYear) and some generated value 
      which is not directly referencing to any other fields based on some custom business logic (autoIncrementCounter) */
      const paramValue = await getParamValue({
        param,
        data,
        field,
        context,
        user,
        tableRowIndex,
      })
      return paramValue
    } else {
      /* this section param itself is value - so simply return param */
      return param
    }
  } catch (error) {
    console.error('Param---', param, error)
    return param
  }
}

const getFieldValue = async ({ key, field, data, listDataIdMetadataMap, dynamicListData }) => {
  if (!data?.allValues && !data?.rowValues) {
    return ''
  }

  const fieldKey = getFieldId(field.id)

  const currentValue =
    data?.rowValues && fieldKey in data.rowValues
      ? data.rowValues[fieldKey]
      : data?.allValues?.[fieldKey]

  if (field?.data_structure === FIELD_TYPES.LIST) {
    const listItemId = currentValue

    if (listItemId == DROPDOWN_DEFAULT_VALUE) {
      return ''
    }

    const isDynamic = field?.list_id == null && field?.dynamic_field_type

    if (isDynamic) {
      const dynType = field.dynamic_field_type?.type

      const sourceList =
        dynType === 'users' ? dynamicListData?.usersList : dynamicListData?.teamsList

      const item = sourceList?.find((it) => it.id == listItemId)

      if (key === COMPUTED_RESERVED_KEYS.VALUE) {
        return item?.value ?? ''
      } else if (key === COMPUTED_RESERVED_KEYS.LIST_DATA_ID) {
        return listItemId
      } else {
        return item?.metadata?.[key] ?? ''
      }
    } else {
      let listDataItem = listDataIdMetadataMap[listItemId]

      if (!listDataItem && field?.list_id) {
        const cacheKey = `list_data_${field.list_id}`
        const cachedListData = await indexedDbStorage.getItem(cacheKey)

        if (cachedListData) {
          const foundItem = cachedListData.find((it) => it.id == listItemId)

          if (foundItem) {
            listDataItem = {
              list_data_id: foundItem.id,
              list_data_value: foundItem.value,
              list_data_metadata: foundItem.metadata,
            }
          }
        }
      }

      if (key === COMPUTED_RESERVED_KEYS.VALUE) {
        return listDataItem?.list_data_value
      } else if (key === COMPUTED_RESERVED_KEYS.LIST_DATA_ID) {
        return listDataItem?.list_data_id || listItemId
      } else {
        return listDataItem?.list_data_metadata?.[key]
      }
    }
  } else if (field?.data_structure === FIELD_TYPES.LIST_MULTI) {
    const rawIds = currentValue

    const selectedIds = Array.isArray(rawIds)
      ? rawIds
      : rawIds
        ? String(rawIds)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : []

    if (selectedIds.length === 0) {
      return ''
    }

    const isDynamic = field?.list_id == null && field?.dynamic_field_type

    const dynType = field?.dynamic_field_type?.type

    const sourceList = isDynamic
      ? dynType === 'users'
        ? dynamicListData?.usersList
        : dynamicListData?.teamsList
      : null

    const results = await Promise.all(
      selectedIds.map(async (id) => {
        if (isDynamic) {
          const item = sourceList?.find((it) => it.id == id)

          if (key === COMPUTED_RESERVED_KEYS.VALUE) {
            return item?.value ?? ''
          } else if (key === COMPUTED_RESERVED_KEYS.LIST_DATA_ID) {
            return id
          } else {
            return item?.metadata?.[key] ?? ''
          }
        } else {
          let listDataItem = listDataIdMetadataMap[id]

          if (!listDataItem && field?.list_id) {
            const cacheKey = `list_data_${field.list_id}`
            const cachedListData = await indexedDbStorage.getItem(cacheKey)

            if (cachedListData) {
              const foundItem = cachedListData.find((it) => it.id == id)

              if (foundItem) {
                listDataItem = {
                  list_data_id: foundItem.id,
                  list_data_value: foundItem.value,
                  list_data_metadata: foundItem.metadata,
                }
              }
            }
          }

          if (key === COMPUTED_RESERVED_KEYS.VALUE) {
            return listDataItem?.list_data_value ?? ''
          } else if (key === COMPUTED_RESERVED_KEYS.LIST_DATA_ID) {
            return listDataItem?.list_data_id || id
          } else {
            return listDataItem?.list_data_metadata?.[key] ?? ''
          }
        }
      }),
    )

    return results.filter(Boolean).join(', ')
  } else {
    let returnValue = currentValue

    if (
      (returnValue === undefined || returnValue === null) &&
      [FIELD_TYPES.CURRENCY, FIELD_TYPES.NUMBER].includes(field?.data_structure)
    ) {
      returnValue = 0
    }

    return returnValue
  }
}

const getParamValue = async ({ param, data, field, context, user, tableRowIndex }) => {
  let computedValue = ''
  const key = param.substring(2)
  if (key === COMPUTED_RESERVED_KEYS.PROCESS_PREFIX) {
    computedValue = context?.processPrefix
  } else if (key === COMPUTED_RESERVED_KEYS.PROCESS_REFERENCE_NUM) {
    computedValue = context?.processReferenceNum
  } else if (key === COMPUTED_RESERVED_KEYS.CURRENT_FINANCIAL_YEAR) {
    computedValue = getCurrentFinancialYear()
  } else if (key === COMPUTED_RESERVED_KEYS.VERSION) {
    computedValue = context?.version
  } else if (key === COMPUTED_RESERVED_KEYS.COUNTER) {
    if (user) {
      const counterRes = await counterService.getCount(
        user,
        data?.allValues[getFieldId(field.metadata.counterFieldTemplateId)],
        data?.allValues[getFieldId(field.metadata.pivotFieldTemplateId)],
        field.id,
        context.processTemplateId,
        context.processReferenceNum,
      )
      computedValue = counterRes?.data?.counter
    }
  } else if (key === COMPUTED_RESERVED_KEYS.SL_NO) {
    computedValue = tableRowIndex
  } else if (key === COMPUTED_RESERVED_KEYS.LOGGED_IN_USER_EMPLOYEE_ID) {
    computedValue = user?.employee_id || ''
  } else if (key === COMPUTED_RESERVED_KEYS.LOGGED_IN_USER_FIRST_NAME) {
    computedValue = user?.first_name || ''
  } else if (key === COMPUTED_RESERVED_KEYS.LOGGED_IN_USER_LAST_NAME) {
    computedValue = user?.last_name || ''
  } else if (key === COMPUTED_RESERVED_KEYS.LOGGED_IN_USER_ID) {
    computedValue = user?.id || ''
  } else if (key === COMPUTED_RESERVED_KEYS.LOGGED_IN_USER_EMAIL) {
    computedValue = user?.email || ''
  } else if (key === COMPUTED_RESERVED_KEYS.LOGGED_IN_USER_DEPARTMENT) {
    computedValue = user?.department || ''
  } else if (key === COMPUTED_RESERVED_KEYS.LOGGED_IN_USER_DESIGNATION) {
    computedValue = user?.designation || ''
  } else if (key === COMPUTED_RESERVED_KEYS.LOGGED_IN_USER_REPORTING_TO) {
    computedValue = user?.reporting_to_user_id || ''
  } else if (key === COMPUTED_RESERVED_KEYS.LOGGED_IN_USER_SUBFUNCTION) {
    computedValue = user?.sub_department || ''
  }
  return computedValue
}

// Resolves any of DATE / DATETIME / TIMESTAMPED_DATE / TIMESTAMPED_DATETIME
// field values into a moment instance, or null if unavailable/unparseable.
const resolveDateLikeMoment = (dataStructure, rawValue) => {
  if (rawValue === undefined || rawValue === null || rawValue === '') return null

  if ([FIELD_TYPES.TIMESTAMPED_DATE, FIELD_TYPES.TIMESTAMPED_DATETIME].includes(dataStructure)) {
    if (!Array.isArray(rawValue) || rawValue.length === 0) return null
    // Entries are prepended on add (newest first) — index 0 is the latest entry
    const latestEntry = rawValue[0]
    const entryFormat =
      dataStructure === FIELD_TYPES.TIMESTAMPED_DATETIME ? FORMATS.DATETIME : FORMATS.DATE
    const parsed = moment(latestEntry?.text, entryFormat, true)
    return parsed.isValid() ? parsed : null
  }

  // Plain DATE / DATETIME fields
  const inputFormat = String(rawValue).match(/^\d{2}-\d{2}-\d{4}$/) ? 'DD-MM-YYYY' : undefined
  const parsed = moment(rawValue, inputFormat)
  return parsed.isValid() ? parsed : null
}

// Counts whole business days between two moments (exclusive of weekends/holidays),
// used only when date_computation_category === 'working-days' for a DATE_DIFF.
const countBusinessDaysBetween = (momentA, momentB, holidayList = []) => {
  const isHoliday = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const day = date.getDate()
    const holidays = holidayList.filter((holiday) => holiday.y === year && holiday.m === month)
    return holidays.some((holiday) => holiday.d.some((d) => d.day === day))
  }

  const earlier = momentA.isBefore(momentB) ? momentA.clone() : momentB.clone()
  const later = momentA.isBefore(momentB) ? momentB.clone() : momentA.clone()

  let count = 0
  const cursor = earlier.clone().startOf('day')
  const end = later.clone().startOf('day')

  while (cursor.isBefore(end)) {
    cursor.add(1, 'day')
    const cursorDate = cursor.toDate()
    if (!isHoliday(cursorDate)) {
      count++
    }
  }

  return count
}
// Resolves a source-date config ({ date_computation_source_type: 'field' | 'activity', ... })
// into a moment instance, or null. Shared by DATE_COMPUTATION and DATE_DIFF.
const resolveSourceDateMoment = async ({
  source_date,
  allValues,
  allActivitiesCreatedAtWithVersion,
  user,
  context,
}) => {
  if (
    source_date?.date_computation_source_type === DATE_COMPUTATION_FIELDS.FIELD &&
    source_date?.field_template_id
  ) {
    // Delegate to resolveDateLikeMoment, which already handles both plain
    // DATE/DATETIME strings and TIMESTAMPED_DATE/TIMESTAMPED_DATETIME arrays.
    // source_date.data_structure may be absent on older configs saved before
    // timestamped types were selectable here — resolveDateLikeMoment's
    // non-timestamped fallback path handles that safely (treats it as a plain
    // date string, same as before).
    const rawValue = allValues[`f_${source_date.field_template_id}`]
    return resolveDateLikeMoment(source_date.data_structure, rawValue)
  }

  if (
    source_date?.date_computation_source_type === DATE_COMPUTATION_FIELDS.ACTIVITY &&
    source_date?.activity_template_id
  ) {
    const res =
      allActivitiesCreatedAtWithVersion ??
      (await processDataService.getActivityVersionInfo(
        user,
        context.processTemplateId,
        context.processReferenceNum,
      ))

    const activityRecord = res?.activityData?.find(
      (it) => it.activity_template_id == source_date.activity_template_id,
    )

    if (!activityRecord) {
      throw new Error(
        `Activity (ID: ${source_date.activity_template_id}) has not been completed yet. Please complete it before proceeding.`,
      )
    }

    const baseDateStr =
      source_date.version_type === ACTIVITY_VERSION.INITIAL_VERSION
        ? activityRecord.initial_version_created_at
        : activityRecord.latest_version_created_at

    if (!baseDateStr) return null

    const inputFormat = String(baseDateStr).match(/^\d{2}-\d{2}-\d{4}$/) ? 'DD-MM-YYYY' : undefined
    const parsedMoment = moment(baseDateStr, inputFormat)

    return parsedMoment.isValid() ? parsedMoment : null
  }

  return null
}

// DATE_COMPUTATION: derives a NEW DATE by adding a duration (from a list's metadata,
// or a plain numeric field) to a source date. Always returns a date string.
const computeDateField = async ({
  fieldMetadata,
  allValues,
  listDataIdMetadataMap,
  allActivitiesCreatedAtWithVersion,
  user,
  context,
  holidayList = [],
}) => {
  const dateConfig = fieldMetadata?.date_config
  if (!dateConfig) return ''

  const { source_date, duration, date_computation_category } = dateConfig

  const startMoment = await resolveSourceDateMoment({
    source_date,
    allValues,
    allActivitiesCreatedAtWithVersion,
    user,
    context,
  })
  if (!startMoment) return ''

  let durationDays = 0
  if (
    duration?.duration_category_type === DATE_COMPUTATION_FIELDS.LIST &&
    duration?.list_field_template_id
  ) {
    const selectedListItemId = allValues[`f_${duration.list_field_template_id}`]

    if (selectedListItemId) {
      const listItem = listDataIdMetadataMap[selectedListItemId]
      const metadata = listItem?.list_data_metadata

      let rawDays = null

      if (duration?.list_metadata_key && metadata && typeof metadata === 'object') {
        rawDays = metadata[duration.list_metadata_key] ?? null
      }

      if (rawDays === null || rawDays === undefined) {
        rawDays = listItem?.list_data_value
      }

      const numericRaw = String(rawDays).match(/\d+(\.\d+)?/)?.[0]
      durationDays = Number(numericRaw) || 0
    }
  } else if (
    duration?.duration_category_type === DATE_COMPUTATION_FIELDS.FIELD &&
    duration?.numeric_field_template_id
  ) {
    const selectedFieldItemId = allValues[`f_${duration.numeric_field_template_id}`]
    durationDays = Number(selectedFieldItemId) || 0
  }

  // CALENDAR DAYS
  if (
    !date_computation_category ||
    date_computation_category === DATE_COMPUTATION_DAYS_CATEGORY.CALENDER_DAYS
  ) {
    const result = startMoment.clone().add(durationDays, 'days')
    return result.isValid() ? result.format(FORMATS.DATE) : ''
  }

  // WORKING DAYS
  if (date_computation_category === DATE_COMPUTATION_DAYS_CATEGORY.WORKING_DAYS) {
    const officeStartTime = 9.5 // 9:30 AM
    const officeEndTime = 18 // 6:00 PM
    const minutesInHour = 60

    const isHoliday = (date) => {
      const year = date.getFullYear()
      const month = date.getMonth() + 1
      const day = date.getDate()
      const holidays = holidayList.filter((holiday) => holiday.y === year && holiday.m === month)
      return holidays.some((holiday) => holiday.d.some((d) => d.day === day))
    }

    const isWeekend = (date) => {
      const day = date.getDay()
      return day === 0 || day === 6
    }

    const getOfficeMinutesInDay = (date) => {
      if (isWeekend(date) || isHoliday(date)) return 0
      return (officeEndTime - officeStartTime) * minutesInHour
    }

    const totalRequiredMinutes = durationDays * (officeEndTime - officeStartTime) * minutesInHour

    let accumulatedMinutes = 0
    let currentDate = startMoment.clone().toDate()

    while (accumulatedMinutes < totalRequiredMinutes) {
      const minutesToday = getOfficeMinutesInDay(currentDate)
      if (minutesToday > 0) {
        accumulatedMinutes += minutesToday
      }
      currentDate.setDate(currentDate.getDate() + 1)
    }
    return moment(currentDate).format(FORMATS.DATE)
  }

  return startMoment.format(FORMATS.DATE)
}

// DATE_DIFF: computes the day-count between two independent date-like values
// (date_a and date_b). Always returns a plain number, never a re-derived date.
// Office-hours constants for DATE_DIFF's working-days calculation when at
// least one side carries a real time-of-day (DATETIME / TIMESTAMPED_DATETIME,
// or an activity-sourced date). A single business day = 9:30 AM–6:00 PM.
const OFFICE_START_HOUR = 9.5
const OFFICE_END_HOUR = 18
const OFFICE_MINUTES_PER_DAY = (OFFICE_END_HOUR - OFFICE_START_HOUR) * 60 // 510

// True if a date_a/date_b config resolves to a value that carries a real
// time-of-day (as opposed to a bare calendar date).
const isDateTimeClassConfig = (dateConfig) => {
  if (dateConfig?.date_computation_source_type === DATE_COMPUTATION_FIELDS.ACTIVITY) return true
  return [FIELD_TYPES.DATETIME, FIELD_TYPES.TIMESTAMPED_DATETIME].includes(
    dateConfig?.data_structure,
  )
}

// Clamps a moment's time-of-day into the office window (9:30 AM–6:00 PM) of
// its OWN calendar day — so a timestamp before/after office hours still
// contributes only its in-office portion to the elapsed-minutes total.
const clampToOfficeWindow = (m) => {
  const start = m.clone().hour(9).minute(30).second(0).millisecond(0)
  const end = m.clone().hour(18).minute(0).second(0).millisecond(0)
  if (m.isBefore(start)) return start
  if (m.isAfter(end)) return end
  return m.clone()
}

// Computes total elapsed BUSINESS minutes between two moments — only the
// portion of each day that falls within office hours (9:30 AM–6:00 PM),
// skipping weekends/holidays entirely. Used for DATE_DIFF's working-days
// output when at least one side has a real time-of-day, so that a gap
// exactly spanning one full office window (9:30 AM to 6:00 PM) equals 1 day.
const countBusinessMinutesBetween = (momentA, momentB, holidayList = []) => {
  const isHoliday = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const day = date.getDate()
    const holidays = holidayList.filter((holiday) => holiday.y === year && holiday.m === month)
    return holidays.some((holiday) => holiday.d.some((d) => d.day === day))
  }

  const start = momentA.isBefore(momentB) ? momentA.clone() : momentB.clone()
  const end = momentA.isBefore(momentB) ? momentB.clone() : momentA.clone()

  if (!start.isBefore(end)) return 0

  let totalMinutes = 0
  const cursorDay = start.clone().startOf('day')
  const endDay = end.clone().startOf('day')

  while (cursorDay.isSameOrBefore(endDay, 'day')) {
    const isFirstDay = cursorDay.isSame(start, 'day')
    const isLastDay = cursorDay.isSame(end, 'day')
    const dateObj = cursorDay.toDate()

    if (!isHoliday(dateObj)) {
      const dayWindowStart = isFirstDay
        ? clampToOfficeWindow(start)
        : clampToOfficeWindow(cursorDay.clone().hour(9).minute(30))
      const dayWindowEnd = isLastDay
        ? clampToOfficeWindow(end)
        : clampToOfficeWindow(cursorDay.clone().hour(18).minute(0))

      const minutes = dayWindowEnd.diff(dayWindowStart, 'minutes')
      if (minutes > 0) totalMinutes += minutes
    }

    cursorDay.add(1, 'day')
  }

  return totalMinutes
}

const computeDateDiffField = async ({
  fieldMetadata,
  allValues,
  allActivitiesCreatedAtWithVersion,
  user,
  context,
  holidayList = [],
}) => {
  const dateDiffConfig = fieldMetadata?.date_diff_config
  if (!dateDiffConfig) return ''

  const { date_a, date_b, date_computation_category, output_unit } = dateDiffConfig
  const unit = output_unit || 'days' // backward-compat default for configs saved before this field existed

  const momentA = await resolveSourceDateMoment({
    source_date: date_a,
    allValues,
    allActivitiesCreatedAtWithVersion,
    user,
    context,
  })
  if (!momentA) return ''

  const rawBValue = allValues[`f_${date_b?.field_template_id}`]
  const momentB = resolveDateLikeMoment(date_b?.data_structure, rawBValue)
  if (!momentB) return ''

  if (date_computation_category === DATE_COMPUTATION_DAYS_CATEGORY.WORKING_DAYS) {
    const hasTimeComponent = isDateTimeClassConfig(date_a) || isDateTimeClassConfig(date_b)

    if (hasTimeComponent) {
      const totalMinutes = countBusinessMinutesBetween(momentA, momentB, holidayList)
      const result = unit === 'hours' ? totalMinutes / 60 : totalMinutes / OFFICE_MINUTES_PER_DAY
      return Number(result.toFixed(1))
    }

    const dayCount = countBusinessDaysBetween(momentA, momentB, holidayList)
    const result = unit === 'hours' ? dayCount * (OFFICE_MINUTES_PER_DAY / 60) : dayCount
    return Number(result.toFixed(1))
  }

  // Calendar days/hours — fractional, so DATETIME/TIMESTAMPED_DATETIME pairs
  // don't silently lose their time-of-day component.
  const diff = Math.abs(momentB.diff(momentA, unit === 'hours' ? 'hours' : 'days', true))
  return Number(diff.toFixed(1))
}
const getDefaultComputeFieldValue = (operation) => {
  switch (operation) {
    case COMPUTED_OPERATION_TYPES.SUM:
    case COMPUTED_OPERATION_TYPES.SUM_TABLE_COLUMN:
      return 0
    case COMPUTED_OPERATION_TYPES.MULTIPLY:
    case COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN:
      return 1
    case COMPUTED_OPERATION_TYPES.CONCAT:
    case COMPUTED_OPERATION_TYPES.CONCAT_TABLE_COLUMN:
      return ''
    default:
      return ''
  }
}

export const getParamId = (param, field) => {
  const isPolarityAppended = [
    COMPUTED_OPERATION_TYPES.SUM,
    COMPUTED_OPERATION_TYPES.MULTIPLY,
  ].includes(field?.metadata?.operation)

  return isPolarityAppended
    ? +param.substring(3, param.indexOf('.'))
    : +param.substring(2, param.indexOf('.'))
}

export const triggerAutoComputeRowWiseOptimized = async ({
  idFieldMap,
  data,
  listDataIdMetadataMap,
  computeFields,
  tableRowId,
  tableRowIndex,
  tableSubfieldsMap,
  selectedTableData,
  dynamicListData,
  context,
  allActivitiesCreatedAtWithVersion,
  holidayList,
  allProcessData,
  user,
}) => {
  /* 
  For tabular computation, these are required to do row wise computation 
  --------
  tableRowId = rowId
  tableRowIndex = rowIndex + 1
  computeFields: computeColumns
  --------
  context = {
    processPrefix,
    processReferenceNum,
    processTemplateId,
    version
  }
  */

  const computedFields = await autoComputeFieldValueOptimized({
    idFieldMap,
    data,
    listDataIdMetadataMap,
    computeFields,
    tableRowId,
    tableRowIndex,
    tableSubfieldsMap,
    selectedTableData,
    dynamicListData,
    context,
    allActivitiesCreatedAtWithVersion,
    holidayList,
    allProcessData,
    user,
  })
  return computedFields
}

/* 

{
  allRows,
  columns,
  allValues,
  tableFieldId,
  listDataIdMetadataMap,
  processContext,
  updatedAt,
  user,
  selectedTableData,
}
*/

export const computeTableValuesOnload = async ({
  allRows,
  columns,
  allValues,
  tableFieldId,
  listDataIdMetadataMap,
  processContext,
  updatedAt,
  user,
  selectedTableData,
}) => {
  const tableKey = getTableFieldId(tableFieldId)

  const computeColumns = columns.filter((column) => column.is_computed)

  const columnIds = new Set(columns.map((column) => column.id))

  const allValueCopy = {
    [tableKey]: { ...(allValues[tableKey] || {}) },
  }

  const ctx = {
    processPrefix: processContext?.processTemplate?.prefix,
    processReferenceNum: processContext?.processData?.process_reference_num,
    processTemplateId: processContext?.processData?.process_template_id,
    version: processContext?.processData?.version,
  }

  const tableRows = allValues?.[tableKey] || {}

  const idFieldMap = Object.fromEntries(
    (processContext?.allFields || []).map((field) => [field.id, field]),
  )

  const allComputedFields =
    computeColumns.length > 0
      ? computeColumns
      : processContext?.allFields?.filter((it) => !it.is_subfield && it.is_computed)

  for (let index = 0; index < allRows?.length; index++) {
    const row = allRows[index]
    const rowKey = `tr_${row}`

    const rowData = tableRows[rowKey] || {}

    // console.time(`triggerAutoComputeRowWise - row ${rowKey}`)

    const rowContext = {
      allValues: allValues,
      rowValues: rowData,
    }

    const autoComputeFieldValues = await triggerAutoComputeRowWiseOptimized({
      idFieldMap: idFieldMap,
      data: rowContext,
      listDataIdMetadataMap,
      computeFields: allComputedFields,
      tableRowId: row,
      tableRowIndex: index + 1,
      tableSubfieldsMap: processContext.tableSubfieldsMap,
      selectedTableData,
      dynamicListData: processContext?.dynamicListData,
      context: ctx,
      allActivitiesCreatedAtWithVersion: null,
      holidayList: null,
      allProcessData: null,
      user,
    })

    const secondPassRowValues = Object.assign({}, rowData, autoComputeFieldValues)

    // Second pass
    const autoComputeFieldValues2 = await triggerAutoComputeRowWiseOptimized({
      idFieldMap: idFieldMap,
      data: {
        allValues: allValues,
        rowValues: secondPassRowValues,
      },
      listDataIdMetadataMap,
      computeFields: allComputedFields,
      tableRowId: row,
      tableRowIndex: index + 1,
      tableSubfieldsMap: processContext.tableSubfieldsMap,
      selectedTableData,
      dynamicListData: processContext?.dynamicListData,
      context: ctx,
      allActivitiesCreatedAtWithVersion: null,
      holidayList: null,
      allProcessData: null,
      user,
    })

    // console.timeEnd(`triggerAutoComputeRowWise - row ${rowKey}`)

    // Ensure row exists
    if (!allValueCopy[tableKey][rowKey]) {
      allValueCopy[tableKey][rowKey] = { updatedAt: updatedAt }
      allValueCopy[tableKey]['updatedAt'] = updatedAt
    }

    for (const key in autoComputeFieldValues2) {
      const value = autoComputeFieldValues2[key]

      const columnId = Number(getIdFromFieldId(key))

      if (!columnIds.has(columnId)) {
        continue
      }

      allValueCopy[tableKey][rowKey][key] = value
      allValueCopy[`${key}__${row}`] = value
    }
  }

  return {
    success: true,
    values: {
      ...allValueCopy,
      updatedAt,
    },
  }
}
